import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from '@themesberg/react-bootstrap'
import { getLotto535Setting, updateLotto535Setting } from '../../../../../controller/api/setting'

const DEFAULT_FORM = {
  saleCutoffOffsetMinutes: 30,
  printCutoffOffsetMinutes: '',
  playConfigs: [],
}

const PLAY_TYPE_LABEL = {
  BASIC: 'Cơ bản',
  SPECIAL_BAO: 'Bao số đặc biệt',
  MAIN_BAO: 'Bao số chính',
}

const toForm = (config) => ({
  saleCutoffOffsetMinutes: config.gameConfig.saleCutoffOffsetSeconds / 60,
  printCutoffOffsetMinutes: config.gameConfig.printCutoffOffsetSeconds == null
    ? '' : config.gameConfig.printCutoffOffsetSeconds / 60,
  playConfigs: config.playConfigs.map((item) => ({ id: item.id, enabled: item.enabled })),
})

// API/DB vẫn dùng giây. Cho phép phút thập phân và làm tròn tới giây gần nhất.
const minutesToSeconds = (value, label, nullable = false) => {
  if (value === '' && nullable) return null
  const minutes = Number(value)
  if (value === '' || !Number.isFinite(minutes) || minutes < 0 || minutes > 1440) {
    throw new Error(`${label} phải từ 0 đến 1.440 phút`)
  }
  return Math.round(minutes * 60)
}

export default function Lotto535Config({ show, onHide, token, refreshKey = 0, cutoffManaged = false }) {
  const [config, setConfig] = useState(null)
  const [loadedRefreshKey, setLoadedRefreshKey] = useState(null)
  const [form, setForm] = useState(DEFAULT_FORM)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result = await getLotto535Setting(token)
      if (!result) throw new Error('Không tải được cấu hình Lotto 5/35')
      setConfig(result)
      setForm(toForm(result))
      setLoadedRefreshKey(refreshKey)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Không tải được cấu hình')
    } finally {
      setLoading(false)
    }
  }, [token, refreshKey])

  useEffect(() => {
    // Cấu hình ít thay đổi: chỉ tải một lần trong phiên của trang; save cập nhật cache này.
    if (show && token && (!config || loadedRefreshKey !== refreshKey)) load()
  }, [show, token, config, loadedRefreshKey, refreshKey, load])

  const toggleItem = (field, id) => {
    setForm((current) => ({
      ...current,
      [field]: current[field].map((item) =>
        item.id === id ? { ...item, enabled: !item.enabled } : item
      ),
    }))
  }

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const flags = config.featureFlags
      const next = await updateLotto535Setting(token, {
        expectedUpdatedAt: config.gameConfig.updatedAt,
        // Các giá trị kỹ thuật không hiển thị vẫn được giữ nguyên khi lưu.
        drawLocalTimes: config.gameConfig.drawLocalTimes,
        saleCutoffOffsetSeconds: minutesToSeconds(form.saleCutoffOffsetMinutes, 'Thời gian ngừng mua'),
        printCutoffOffsetSeconds: minutesToSeconds(form.printCutoffOffsetMinutes, 'Thời gian ngừng in', true),
        futurePeriodHorizonDays: config.gameConfig.futurePeriodHorizonDays,
        playConfigs: form.playConfigs.map(({ id, enabled }) => ({ id, enabled })),
        expectedFeatureFlags: flags,
        runtimeFeatureFlags: {
          periodEnabled: flags.periodEnabled,
          cartEnabled: flags.cartEnabled,
          saleMode: flags.saleMode,
          resultEnabled: flags.resultEnabled,
          allowlistedUserIds: flags.allowlistedUserIds,
        },
      })
      if (!next) throw new Error('Không lưu được cấu hình Lotto 5/35')
      setConfig(next)
      setForm(toForm(next))
      onHide()
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.code || err.message || 'Không lưu được cấu hình')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Cấu hình Lotto 5/35</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading || !config ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            {!loading && error && (
              <div className="mt-3"><Button variant="outline-primary" onClick={load}>Thử lại</Button></div>
            )}
          </div>
        ) : (
          <>
            <Alert variant="info">
              Bật hoặc tắt Lotto 5/35 bằng công tắc Trạng thái ở danh sách trò chơi.
              Tự động tính và trả thưởng được quản lý tại màn Tự động tính thưởng.
            </Alert>

            {cutoffManaged ? <Alert variant="info">Ba mốc mua/in/upload đang quản lý tại nút Chỉnh 3 mốc trong danh sách Trò chơi.</Alert> : <Row className="g-3 mb-4">
              <Col md={6}>
                <Form.Label>Khách ngừng mua trước kỳ (phút)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={1440}
                  step="any"
                  value={form.saleCutoffOffsetMinutes}
                  onChange={(e) => setForm({ ...form, saleCutoffOffsetMinutes: e.target.value })}
                />
                <Form.Text className="text-muted">
                  Số phút trước giờ xổ của kỳ quay. Ví dụ: 30 phút, kỳ xổ 21:00 thì ngừng mua lúc 20:30.
                </Form.Text>
              </Col>
              <Col md={6}>
                <Form.Label>Nhân viên ngừng in trước kỳ (phút)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={1440}
                  step="any"
                  placeholder="Chưa cấu hình"
                  value={form.printCutoffOffsetMinutes}
                  onChange={(e) => setForm({ ...form, printCutoffOffsetMinutes: e.target.value })}
                />
                <Form.Text className="text-muted">
                  Số phút trước giờ xổ của kỳ quay. Ví dụ: 2 phút, kỳ xổ 21:00 thì ngừng in lúc 20:58.
                  Để trống nếu chưa cấu hình.
                </Form.Text>
              </Col>
            </Row>}

            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h6 className="mb-1">Cấu hình nâng cao</h6>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Chi tiết cách chơi ít khi thay đổi. Giá mỗi tổ hợp cố định 10.000đ.
                </div>
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setAdvancedOpen((current) => !current)}
              >
                {advancedOpen ? 'Thu gọn' : 'Mở cấu hình nâng cao'}
              </Button>
            </div>

            {advancedOpen && (
              <>
                <h6>Chi tiết cách chơi</h6>
                <Table responsive size="sm" className="mb-0">
                  <thead>
                    <tr><th>Loại</th><th>Số chính</th><th>Số đặc biệt</th><th>Số bộ</th><th>Bật</th></tr>
                  </thead>
                  <tbody>
                    {config.playConfigs.map((item) => {
                      const state = form.playConfigs.find((entry) => entry.id === item.id)
                      return (
                        <tr key={item.id}>
                          <td>{PLAY_TYPE_LABEL[item.playType] || item.playType}</td>
                          <td>{item.mainNumberCount}</td>
                          <td>{item.specialNumberCount}</td>
                          <td>{item.combinationCount}</td>
                          <td>
                            <Form.Check
                              type="switch"
                              checked={!!state?.enabled}
                              onChange={() => toggleItem('playConfigs', item.id)}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Đóng</Button>
        <Button variant="primary" disabled={loading || saving || !config} onClick={save}>
          {saving ? 'Đang lưu...' : 'Lưu cấu hình'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
